import api from '@/services/api'
import type { PjAgrada, PjAgradaFormData, PjAgradaStatus } from '@/components/PjAgrada/pjAgradaTypes'
import { onlyDigits } from '@/components/PjAgrada/pjAgradaFormat'
import { EMPTY_PJ_AGRADA_FORM } from '@/components/PjAgrada/pjAgradaTypes'

type ListParams = {
  search?: string
  status?: PjAgradaStatus | 'todos'
  page?: number
  limit?: number
}

type ListResponse = {
  data: PjAgrada[]
  meta: {
    page: number
    limit: number
    totalItems: number
    totalPages: number
    stats: { ativos: number; inativos: number; naoPreenchidos: number; naoConcluidos: number; total: number }
  }
}

function toPayload(form: PjAgradaFormData) {
  return {
    nomeCompleto: form.nomeCompleto.trim(),
    email: form.email.trim() || undefined,
    telefone: form.telefone.trim(),
    cpf: onlyDigits(form.cpf) || undefined,
    cnpj: onlyDigits(form.cnpj) || undefined,
    razaoSocial: form.razaoSocial.trim() || undefined,
    cep: onlyDigits(form.cep) || undefined,
    logradouro: form.logradouro.trim() || undefined,
    numero: form.numero.trim() || undefined,
    complemento: form.complemento.trim() || undefined,
    bairro: form.bairro.trim() || undefined,
    cidade: form.cidade.trim() || undefined,
    uf: form.uf.trim().toUpperCase() || undefined,
    bancoNome: form.bancoNome.trim() || undefined,
    bancoAgencia: form.bancoAgencia.trim() || undefined,
    bancoConta: form.bancoConta.trim() || undefined,
    bancoTipoConta: form.bancoTipoConta || undefined,
    pixChave: form.pixChave.trim() || undefined,
    areaAtuacao: form.areaAtuacao.trim() || undefined,
    dataInicioContrato: form.dataInicioContrato || undefined,
    status: form.status,
    observacoes: form.observacoes.trim() || undefined,
    portalUserId: form.portalUserId.trim() ? form.portalUserId.trim() : null,
  }
}

export const pjAgradaService = {
  async list(params: ListParams = {}): Promise<ListResponse> {
    const resp = await api.get('/pj-agrada', { params })
    return {
      data: resp.data.data ?? [],
      meta: resp.data.meta,
    }
  },

  async getById(id: string): Promise<PjAgrada> {
    const resp = await api.get(`/pj-agrada/${id}`)
    return resp.data.data
  },

  async create(form: PjAgradaFormData): Promise<PjAgrada> {
    const resp = await api.post('/pj-agrada', toPayload(form))
    return resp.data.data
  },

  async update(id: string, form: PjAgradaFormData): Promise<PjAgrada> {
    const resp = await api.patch(`/pj-agrada/${id}`, toPayload(form))
    return resp.data.data
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/pj-agrada/${id}`)
  },

  async createInvite(input: {
    nomeCompleto?: string
    email?: string
    areaAtuacao?: string
    expiresInHours?: number
  }): Promise<{
    id: string
    pjAgradaId: string
    url: string
    expiresAt: string
    nomeCompleto?: string | null
    email?: string | null
    areaAtuacao?: string | null
  }> {
    const resp = await api.post('/pj-agrada/invites', input)
    return resp.data.data
  },

  async getCadastroInvite(token: string): Promise<{
    nomeCompleto?: string | null
    email?: string | null
    emailHint?: string | null
    emailLocked?: boolean
    areaAtuacao?: string | null
    expiresAt: string
  }> {
    const resp = await api.get(`/pj-agrada/cadastro/${token}`)
    return resp.data.data
  },

  async completeCadastro(
    token: string,
    payload: Omit<PjAgradaFormData, 'id' | 'status' | 'observacoes' | 'areaAtuacao' | 'dataInicioContrato'>,
    honeypot = '',
  ): Promise<{ id: string; nomeCompleto: string }> {
    const resp = await api.post(`/pj-agrada/cadastro/${token}`, {
      ...toPayload({
        ...EMPTY_PJ_AGRADA_FORM,
        ...payload,
        status: 'ATIVO',
        observacoes: '',
        areaAtuacao: '',
        dataInicioContrato: '',
      }),
      website: honeypot,
    })
    return resp.data.data
  },
}
